/**
 * ArtDirectionImage Component
 *
 * A React component leveraging the picture element to enable
 * art direction for responsive images. Dynamically selects and displays
 * the best image source based on media queries, ensuring optimized visuals
 * across different viewport sizes. Includes Tailwind CSS support,
 * event handling, and a customizable API.
 *
 * @author Team CMS
 */
'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { cn } from '../../../utils/index.js';
import { generateSrcsetString } from '../../core/utils/generateSrcsetString.js';
import { getDefaultImgUrl } from '../../core/utils/getDefaultImgUrl.js';
/**
 * ArtDirectionImage Component.
 *
 * @param props - Component properties.
 * @param props.sources - Array of image sources with media queries and dimensions.
 * @param props.fallbackSrc - Fallback image source URL.
 * @param props.className - Additional CSS class names.
 * @param props.shouldFitWrapper - Whether the image should fit its wrapper.
 * @param props.loading - Loading attribute for the image.
 * @param props.decoding - Decoding attribute for the image.
 * @param props.backgroundColor - Background color for the image container.
 * @param props.onLoad - Callback function for image load event.
 * @param props.onError - Callback function for image error event.
 * @returns A JSX element representing the art direction image.
 */
export default function ArtDirectionImage({ sources, fallbackSrc, className = '', shouldFitWrapper = false, loading = 'eager', decoding = 'auto', backgroundColor, onLoad, onError, }) {
    const [currentSource, setCurrentSource] = useState(null);
    const [isClient, setIsClient] = useState(false);
    const [supportsAspectRatio, setSupportsAspectRatio] = useState(false);
    // Sort sources by size in ascending order
    const sortedSources = useMemo(() => sources.slice().sort((a, b) => a.width * a.height - b.width * b.height), [sources]);
    const firstSource = sortedSources[0] ?? null;
    // Calculate aspect ratio dynamically based on currentSource or fallback to first source
    const aspectRatio = useMemo(() => {
        const source = currentSource || firstSource;
        return source?.width && source?.height ? source.width / source.height : undefined;
    }, [currentSource, firstSource]);
    const resolveFallbackSrc = useMemo(() => getDefaultImgUrl(fallbackSrc, firstSource?.requestedWidths, firstSource?.width), [fallbackSrc, firstSource?.requestedWidths, firstSource?.width]);
    // Set up client-side check and resize listener
    useEffect(() => {
        setIsClient(true);
        setSupportsAspectRatio(CSS.supports('aspect-ratio', '1 / 1'));
        const updateCurrentSource = () => {
            const matchedSource = sortedSources.find((source) => source.media ? window.matchMedia(source.media).matches : true);
            setCurrentSource(matchedSource ?? firstSource);
        };
        updateCurrentSource(); // Initialize on mount
        window.addEventListener('resize', updateCurrentSource);
        return () => window.removeEventListener('resize', updateCurrentSource);
    }, [sortedSources]);
    // Image elements for the picture element
    const renderImage = () => {
        if (!isClient || !currentSource) {
            return _jsx("img", { src: resolveFallbackSrc, alt: "Image", className: "h-full w-full object-cover" });
        }
        const imgProps = {
            src: resolveFallbackSrc,
            alt: currentSource.alt || 'Image',
            width: currentSource.width,
            height: currentSource.height,
            loading,
            className: cn('w-full', shouldFitWrapper && 'h-full object-cover'),
            onLoad,
            onError,
            decoding,
            title: currentSource.title,
        };
        return _jsx("img", { ...imgProps });
    };
    return (_jsxs("div", { className: cn('relative block h-full w-full', className), style: {
            backgroundColor,
            ...(supportsAspectRatio
                ? { aspectRatio: aspectRatio ? `${aspectRatio}` : undefined }
                : { paddingTop: `${aspectRatio ? (1 / aspectRatio) * 100 : 100}%` }),
        }, itemScope: true, itemType: "https://schema.org/ImageObject", children: [_jsx("meta", { content: resolveFallbackSrc, itemProp: "url" }), _jsxs("picture", { children: [sortedSources.map(({ src, media, width, requestedWidths }, index) => {
                        const srcSet = generateSrcsetString(src, requestedWidths, width);
                        return srcSet ? _jsx("source", { srcSet: srcSet, media: media }, index) : null;
                    }), renderImage()] })] }));
}
//# sourceMappingURL=ArtDirectionImage.js.map